import { TriangleAlertIcon, XIcon } from 'lucide-react';
import * as React from 'react';

import { DimeAlert, DimeAlertAction, DimeAlertDescription, DimeAlertTitle } from './DimeAlert.js';
import { DimeButton } from './DimeButton.js';

type DimeAlertToastProps = Readonly<{
  title: React.ReactNode;
  description?: React.ReactNode;
  onClose: () => void;
  /** Names the close button, e.g. "Close possible duplicates". */
  closeLabel: string;
  children?: React.ReactNode;
  /** Takes focus whenever this changes to a number, e.g. when the user asked to see the toast. */
  focusKey?: number;
}>;

/** An alert for a toast that stays until closed (`toast.custom` with `duration: Infinity`); it takes focus when asked, e.g. when the user wants to see it again. */
export function DimeAlertToast({
  title,
  description,
  onClose,
  closeLabel,
  children,
  focusKey,
}: DimeAlertToastProps) {
  const titleId = React.useId();
  const [alertEl, setAlertEl] = React.useState<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (focusKey !== undefined) alertEl?.focus();
  }, [focusKey, alertEl]);

  return (
    <DimeAlert
      ref={setAlertEl}
      variant="warning"
      role="region"
      tabIndex={-1}
      aria-labelledby={titleId}
      // Only the title makes room for the close button; everything below it uses the full width.
      className="w-full shadow-lg has-data-[slot=alert-action]:pr-2.5"
    >
      <TriangleAlertIcon aria-hidden />
      <DimeAlertTitle id={titleId} className="pr-7">
        {title}
      </DimeAlertTitle>
      {description ? <DimeAlertDescription>{description}</DimeAlertDescription> : null}
      <DimeAlertAction>
        <DimeButton
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={closeLabel}
          onClick={onClose}
        >
          <XIcon aria-hidden="true" />
        </DimeButton>
      </DimeAlertAction>
      {children ? (
        <div className="col-span-full mt-2 flex flex-col gap-2 text-foreground">{children}</div>
      ) : null}
    </DimeAlert>
  );
}
